import { useState } from 'react';
import { format } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Icon } from '@/lib/icons';
import type { ThemeImportItem } from '@/api/theme-import-history.contract';
import {
  ThemeImportQuestionsDialog,
  type ThemeImportQuestionsDialogProps,
} from './ThemeImportQuestionsDialog';
import { ThemeQuestionEditorDialog } from './ThemeQuestionEditorDialog';

interface ThemeImportRowProps {
  row: ThemeImportItem;
  labels: {
    unknownAuthor: string;
    active: string;
    inactive: string;
    previewQuestions: string;
    editQuestions: string;
    updateQuestions: string;
    delete: string;
    cancel: string;
    deleteTitle: string;
    deleteDescription: string;
    questionsDialog: ThemeImportQuestionsDialogProps['copy'];
  };
  isDeleting: boolean;
  onDelete: (id: string) => void;
}

export function ThemeImportRow({ row, labels, isDeleting, onDelete }: ThemeImportRowProps) {
  const [isQuestionsOpen, setIsQuestionsOpen] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'edit' | 'update'>('edit');

  return (
    <article className="group col-span-full grid grid-cols-subgrid border-b border-border/40 py-5 last:border-b-0">
      <div className="col-span-full grid grid-cols-subgrid items-center">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-foreground" title={row.filenames.join(', ')}>
            {row.filenames.join(', ')}
          </h3>
        </div>

        <div className="min-w-0">
          <span className="truncate text-sm text-foreground/90">{row.title}</span>
        </div>

        <div className="min-w-0">
          <span className="truncate text-sm text-foreground/90">{row.sectorName}</span>
        </div>

        <div>
          <span className="text-sm tabular-nums text-muted-foreground">{row.questionsCount.toLocaleString()}</span>
        </div>

        <div className="flex min-w-0 items-center gap-2">
          <div className="h-6 w-6 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
            {row.uploadedBy?.username?.[0]?.toUpperCase() || '?'}
          </div>
          <span className="truncate text-sm text-muted-foreground">
            {row.uploadedBy?.username ?? labels.unknownAuthor}
          </span>
        </div>

        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {format(new Date(row.createdAt), 'dd MMM yyyy · HH:mm')}
        </span>

        <div>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[0.62rem] font-semibold uppercase tracking-[0.14em] ${
              row.status === 'active'
                ? 'bg-success/10 text-success dark:text-success'
                : 'bg-muted/30 text-muted-foreground'
            }`}
          >
            {row.status === 'active' ? labels.active : labels.inactive}
          </span>
        </div>

        <div className="flex items-center justify-end gap-3">
          {row.status === 'active' && <button type="button" onClick={() => { setEditorMode('edit'); setIsEditorOpen(true); }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground/80 hover:text-foreground">
            <Icon name="edit" className="h-3.5 w-3.5" />{labels.editQuestions}
          </button>}
          <ThemeQuestionEditorDialog themeId={row.id} themeTitle={row.title} themeSectorName={row.sectorName}
            open={isEditorOpen} onOpenChange={setIsEditorOpen} mode={editorMode} />
          <button
            type="button"
            aria-label={labels.previewQuestions}
            onClick={() => setIsQuestionsOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground/80 transition-colors hover:text-foreground"
          >
            <Icon name="eye" className="h-3.5 w-3.5" />
          </button>

          <ThemeImportQuestionsDialog
            themeId={row.id}
            themeTitle={row.title}
            themeSectorName={row.sectorName}
            open={isQuestionsOpen}
            onOpenChange={setIsQuestionsOpen}
            copy={labels.questionsDialog}
          />

          {row.status === 'active' && <button type="button" aria-label={labels.updateQuestions}
            onClick={() => { setEditorMode('update'); setIsEditorOpen(true); }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground/80 hover:text-foreground">
            <Icon name="upload" className="h-3.5 w-3.5" />
          </button>}

          {row.status === 'active' && <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                disabled={isDeleting}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-danger transition-colors hover:text-danger disabled:cursor-not-allowed disabled:opacity-35"
              >
                {isDeleting ? (
                  <Icon name="loader2" className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Icon name="xCircle" className="h-3.5 w-3.5" />
                )}
                <span className="sr-only">{labels.delete}</span>
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="border-border/40 bg-card">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-foreground">{labels.deleteTitle}</AlertDialogTitle>
                <AlertDialogDescription className="text-muted-foreground">
                  {labels.deleteDescription}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-border/40 bg-transparent text-muted-foreground hover:bg-muted/20">
                  {labels.cancel}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(row.id)}
                  disabled={isDeleting}
                  className="bg-danger/20 text-danger hover:bg-danger/30"
                >
                  {labels.delete}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>}
        </div>
      </div>
    </article>
  );
}
